/* eslint-disable no-undef */
// eslint-disable-next-line no-unused-vars
import React, { useEffect, useRef } from 'react';

/**
 * Helper function to post a resize message to the parent frame formatted in
 * such a way that it will work with AP's resizing logic on various platforms.
 * Allow a `resize` ID to be set via a URL hash parameter like so:
 *
 * ```
 * http://domain.com/embed/path.html#resize=<my-id>
 * ```
 *
 * When provided, the `resize` ID will be included in the resize message to
 * masquerade as a Datawrapper embed.
 */
export function fireResizeMessage(currentScroll = false) {
  const params = new URLSearchParams(window.location.hash.slice(1));
  const resize = params.get('resize');
  const height = document.documentElement.offsetHeight;
  const data = {
    sentinel: 'amp',
    type: 'embed-size',
    height,
    currentScroll,
  };
  if (resize) {
    data['datawrapper-height'] = {
      [resize]: height,
    };
  }
  window.parent.postMessage(data, '*');
}

/**
 * Leaf component that should be included for side effects only with no
 * children. Adds resize message posting logic to the component tree.
 */
function ApResize() {
  const observer = useRef();

  // Fire resize message whenever document element changes size
  useEffect(() => {
    observer.current = new window.ResizeObserver(() => {
      fireResizeMessage();
    });
    observer.current.observe(document.documentElement);
    return () => observer.current.disconnect();
  }, []);

  // Handle size query messages from parent to handle embed/resize script race
  // condition
  useEffect(() => {
    const handler = (msg) => {
      if (msg.type !== 'embed-size-query') return;
      fireResizeMessage();
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return null;
}

export default ApResize;
